import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { LeadSourceSearchableSelect } from "@/components/common/LeadSourceSearchableSelect";
import { leadSourceNeedsDetails } from "@/lib/leadSources";
import { patientApi } from "@/api/patient";

interface RegistrationSourceCaptureDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	patientId: string;
	patientName?: string;
	/** Called after source is saved successfully. */
	onCaptured?: () => void;
}

export default function RegistrationSourceCaptureDialog({
	open,
	onOpenChange,
	patientId,
	patientName,
	onCaptured,
}: RegistrationSourceCaptureDialogProps) {
	const queryClient = useQueryClient();
	const [registrationSource, setRegistrationSource] = useState("");
	const [registrationSourceDetails, setRegistrationSourceDetails] = useState("");

	const captureMutation = useMutation({
		mutationFn: () =>
			patientApi.captureRegistrationSource(patientId, {
				registrationSource,
				registrationSourceDetails: registrationSourceDetails.trim() || undefined,
			}),
		onSuccess: () => {
			toast.success("Registration source saved");
			void queryClient.invalidateQueries({ queryKey: ["patients"] });
			void queryClient.invalidateQueries({ queryKey: ["hospital-patients"] });
			void queryClient.invalidateQueries({ queryKey: ["patient", patientId] });
			void queryClient.invalidateQueries({ queryKey: ["appointment"] });
			setRegistrationSource("");
			setRegistrationSourceDetails("");
			onOpenChange(false);
			onCaptured?.();
		},
		onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
			toast.error(
				err.response?.data?.message || err.message || "Failed to save registration source"
			);
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!registrationSource.trim()) {
			toast.error("Please select how the patient heard about the hospital.");
			return;
		}
		if (
			leadSourceNeedsDetails(registrationSource) &&
			!registrationSourceDetails.trim()
		) {
			toast.error("Please enter source details.");
			return;
		}
		captureMutation.mutate();
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Registration source required</DialogTitle>
					<DialogDescription>
						{patientName ? (
							<>
								<span className="font-medium">{patientName}</span> self-registered online.
								Please record how they heard about the hospital before proceeding.
							</>
						) : (
							<>
								This patient self-registered online. Please record how they heard about the
								hospital before proceeding.
							</>
						)}
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={handleSubmit} className="space-y-4 pt-2">
					<div className="space-y-2">
						<Label>How did the patient hear about us? *</Label>
						<LeadSourceSearchableSelect
							value={registrationSource}
							onValueChange={(val) => {
								setRegistrationSource(val);
								if (!leadSourceNeedsDetails(val)) {
									setRegistrationSourceDetails("");
								}
							}}
							disabled={captureMutation.isPending}
							placeholder="Select registration source"
							searchPlaceholder="Search registration source…"
						/>
					</div>
					{leadSourceNeedsDetails(registrationSource) && (
						<div className="space-y-2">
							<Label>Details *</Label>
							<Input
								value={registrationSourceDetails}
								onChange={(e) => setRegistrationSourceDetails(e.target.value)}
								placeholder={
									registrationSource === "REFERRAL"
										? "Referrer name or details"
										: "Enter details"
								}
								disabled={captureMutation.isPending}
							/>
						</div>
					)}
					<div className="flex justify-end gap-2 pt-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={captureMutation.isPending}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={captureMutation.isPending}>
							{captureMutation.isPending ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Saving…
								</>
							) : (
								"Save & continue"
							)}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
}
